import type { JSONContent } from "@tiptap/core";

import { buildPath } from "@/react/helpers/urlHelpers/internalApi/buildPath";

import type { ActiveMemory } from "./contextPanelTypes";
import type { ConversationData, RunState } from "./storeHelpers";
import type { PageContext } from "./usePageContext";

export type PostCompletionResult =
  | { conversationId: string; runState: RunState | null | undefined }
  | { error: string; message?: string; runState?: RunState | null };

interface PostCompletionParams {
  attachments?: string[];
  briefId?: number;
  content: string;
  conversationId: string | null;
  pageContext?: PageContext;
  projectId?: string;
  tiptapBody?: JSONContent;
  token: string;
}

export type PostVoiceTranscriptionResult =
  | { text: string }
  | { error: string; message?: string };

const FALLBACK_AUDIO_EXTENSION = "webm";

const AUDIO_EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  "audio/mp3": "mp3",
  "audio/mp4": "m4a",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/webm": "webm",
};

const getAudioExtension = (contentType: string) =>
  AUDIO_EXTENSION_BY_MIME_TYPE[contentType.split(";")[0] ?? ""] ??
  FALLBACK_AUDIO_EXTENSION;

export const postCompletion = async ({
  attachments = [],
  briefId,
  content,
  conversationId,
  pageContext,
  projectId,
  tiptapBody,
  token,
}: PostCompletionParams): Promise<PostCompletionResult> => {
  const response = await fetch("/internal_api/circle_ai/completions", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    body: JSON.stringify({
      content,
      conversation_id: conversationId,
      ...(tiptapBody ? { tiptap_body: tiptapBody } : {}),
      ...(attachments.length ? { attachments } : {}),
      ...(projectId ? { project_id: projectId } : {}),
      ...(briefId ? { brief_id: briefId } : {}),
      ...(pageContext ? { page_context: pageContext } : {}),
    }),
  });
  if (response.ok) {
    const data = (await response.json()) as {
      conversation_id: string;
      run_state?: RunState | null;
    };
    return { conversationId: data.conversation_id, runState: data.run_state };
  }
  try {
    const data = (await response.json()) as {
      error?: string;
      message?: string;
      run_state?: RunState | null;
    };
    return {
      error: data.error ?? "unknown",
      message: data.message,
      runState: data.run_state,
    };
  } catch {
    return { error: "unknown" };
  }
};

export const postVoiceTranscription = async (
  audio: Blob,
  token: string,
): Promise<PostVoiceTranscriptionResult> => {
  const formData = new FormData();
  const extension = getAudioExtension(audio.type);
  formData.append("audio", audio, `circle-ai-voice-message.${extension}`);

  const response = await fetch("/internal_api/circle_ai/transcriptions", {
    method: "POST",
    headers: { "X-CSRF-Token": token },
    body: formData,
  });

  try {
    const data = (await response.json()) as {
      error?: string;
      message?: string;
      text?: string;
    };

    if (response.ok) return { text: data.text ?? "" };

    return {
      error: data.error ?? "unknown",
      message: data.message,
    };
  } catch {
    return { error: "unknown" };
  }
};

export const createOnboardingConversation = async (
  token: string,
): Promise<string | null> => {
  const response = await fetch(
    "/internal_api/circle_ai/onboarding_conversation",
    {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    },
  );
  if (!response.ok) return null;
  const data = (await response.json()) as { conversation_id: string };
  return data.conversation_id;
};

export const fetchConversation = async (
  uuid: string,
  token: string,
  params: {
    message_id?: number;
    per_page?: number;
  } = {},
): Promise<ConversationData | null> => {
  const pathParams = {
    ...(params.message_id != null ? { message_id: params.message_id } : {}),
    ...(params.per_page != null ? { per_page: params.per_page } : {}),
  };
  const basePath = `/internal_api/circle_ai/conversations/${uuid}`;
  const path =
    Object.keys(pathParams).length > 0
      ? buildPath(basePath, pathParams)
      : basePath;
  const response = await fetch(path, { headers: { "X-CSRF-Token": token } });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error("Failed to fetch conversation");
  return (await response.json()) as ConversationData;
};

export interface OnboardingConversationData {
  conversationId: string;
  lastMessagePreview: string | null;
  title?: string | null;
}

export const postCancel = async (
  uuid: string,
  token: string,
): Promise<{ run_state: RunState } | null> => {
  const response = await fetch(
    `/internal_api/circle_ai/conversations/${uuid}/cancel`,
    { method: "POST", headers: { "X-CSRF-Token": token } },
  );
  if (!response.ok) return null;
  return (await response.json()) as { run_state: RunState };
};

export const fetchMemories = async (token: string): Promise<ActiveMemory[]> => {
  const response = await fetch(`/internal_api/circle_ai/memories`, {
    headers: { "X-CSRF-Token": token },
  });
  if (!response.ok) return [];
  return (await response.json()) as ActiveMemory[];
};
